import { FC } from 'react';

import { useFormik } from 'formik';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';

import { BroadcastForm } from '../BroadcastForm/BroadcastForm';
import { BroadcastFormValues, initialValues, validationSchema } from '../BroadcastForm/form';
import { NOTIFICATION_LOCALES } from '../constants';

import css from './BroadcastNotifications.module.css';

import { broadcastNotificationService } from 'v2/api/broadcastNotificationService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { fileSrc, handleHttpError } from 'v2/utils';

interface BroadcastNotificationsProps {
  onSuccess?: VoidFunction;
}

export const BroadcastNotifications: FC<BroadcastNotificationsProps> = ({ onSuccess }) => {
  const { trigger: broadcast, isMutating: isBroadcasting } = useSWRMutation(
    'broadcastNotification',
    broadcastNotificationService.broadcast
  );

  const formik = useFormik<BroadcastFormValues>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      const localeData = {
        ...values.content,
        ...(values.iconId ? { iconId: values.iconId } : {}),
      };
      const data = Object.fromEntries(NOTIFICATION_LOCALES.map((locale) => [locale.code, localeData]));

      const metadata: Record<string, string> | null =
        values.metadata.length > 0
          ? Object.fromEntries(values.metadata.filter((m) => m.key).map((m) => [m.key, m.value]))
          : null;

      try {
        const result = await broadcast({
          categorySubType: values.categorySubType,
          data,
          metadata,
          isPinned: values.isPinned,
        });

        toast.success(`Notification sent to ${result?.usersNotified ?? 0} users`);

        resetForm();
        onSuccess?.();
      } catch (error) {
        handleHttpError(error);
      }
    },
  });

  const handleSubmit = () => {
    if (!canSubmit) formik.validateForm();
  };

  const { content } = formik.values;
  const canSubmit =
    formik.values.iconId &&
    formik.values.categorySubType &&
    content.title &&
    content.description &&
    content.buttonText &&
    content.link;

  return (
    <div className={css.broadcastLayout}>
      <form className={css.formContainer} onSubmit={formik.handleSubmit}>
        <BroadcastForm formik={formik} />

        <div className={css.submitSection}>
          <div onClick={handleSubmit}>
            <Popconfirm
              message="Send notification to all users?"
              confirmText="Send"
              disabled={!canSubmit || isBroadcasting}
              onConfirm={() => formik.submitForm()}
            >
              <Button
                type="button"
                variant="contained"
                disabled={!canSubmit || isBroadcasting}
                size="large"
                startIcon={<Send size={16} />}
              >
                {isBroadcasting ? 'Sending...' : 'Broadcast Notification'}
              </Button>
            </Popconfirm>
          </div>
        </div>
      </form>

      <div className={css.previewContainer}>
        <div className={css.previewHeader}>Notification Preview</div>

        <div className={css.previewBackground}>
          <div className={css.notificationCard}>
            <div className={css.cardContent}>
              {formik.values.iconId && (
                <div className={css.imageContainer}>
                  <img src={fileSrc(formik.values.iconId)} alt="Icon" className={css.rewardImage} />
                </div>
              )}

              <div className={css.notificationTextContent}>
                <p className={css.notificationTitle}>
                  {content.title || <span className={css.previewPlaceholder}>Title</span>}
                </p>
                <p className={css.notificationDescription}>
                  {content.description || <span className={css.previewPlaceholder}>Description</span>}
                </p>
              </div>

              <div className={css.claimButton}>
                {content.buttonText || <span className={css.previewPlaceholder}>Button text</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
